import React, { useState, useContext, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  Image,
  Switch,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AuthContext } from '../context/AuthContext';
import Button from '../components/Button';
import Input from '../components/Input';
import APIService from '../services/api';
import { User } from '../types';
import { StackNavigationProp } from '@react-navigation/stack';

interface ProfileScreenProps {
  navigation: StackNavigationProp<any>;
}

interface ProfileStats {
  totalPurchases: number;
  walletBalance: number;
  referredCount: number;
}

export const ProfileScreen: React.FC<ProfileScreenProps> = ({ navigation }) => {
  const { user, logout, updateUser } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [stats, setStats] = useState<ProfileStats>({
    totalPurchases: 0,
    walletBalance: 0,
    referredCount: 0,
  });
  const [editedUser, setEditedUser] = useState<Partial<User>>({
    name: user?.name || '',
    email: user?.email || '',
    telefono: user?.telefono || user?.phone || '',
    direccion: user?.direccion || '',
    identificacion: user?.identificacion || '',
    nit: user?.nit || '',
    edad: user?.edad || undefined,
  });
  const [pushNotifications, setPushNotifications] = useState(true);

  useEffect(() => {
    if (user) {
      loadUserStats();
    }
  }, [user]);

  const loadUserStats = async () => {
    try {
      setLoadingStats(true);
      // Obtener saldo de billetera virtual
      const walletResponse = await APIService.getWalletBalance();
      
      // Obtener conteo de referidos
      let referredCount = 0;
      try {
        const referidosResponse = await APIService.getReferidos();
        referredCount = referidosResponse?.length || 0;
      } catch (error) {
        console.warn('No se pudo cargar conteo de referidos:', error);
      }
      
      setStats({
        totalPurchases: 0, // Se puede obtener del endpoint cuando esté disponible
        walletBalance: walletResponse?.saldo_billetera ? parseFloat(walletResponse.saldo_billetera) : 0,
        referredCount: referredCount,
      });
    } catch (error) {
      console.error('Error loading user stats:', error);
      setStats({
        totalPurchases: 0,
        walletBalance: 0,
        referredCount: 0,
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const handleEditProfile = () => {
    setIsEditing(true);
    setEditedUser({
      name: user?.name || '',
      email: user?.email || '',
      telefono: user?.telefono || user?.phone || '',
      direccion: user?.direccion || '',
      identificacion: user?.identificacion || '',
      nit: user?.nit || '',
      edad: user?.edad || undefined,
    });
  };

  const handleSaveProfile = async () => {
    if (!editedUser.name?.trim()) {
      Alert.alert('Error', 'El nombre es requerido');
      return;
    }

    setLoading(true);
    try {
      const updatedUser = await APIService.updateProfile(editedUser);
      
      if (updateUser) {
        updateUser(updatedUser);
      }
      
      setIsEditing(false);
      Alert.alert('Éxito', 'Perfil actualizado correctamente');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      Alert.alert(
        'Error', 
        error.message || 'Error al actualizar el perfil'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedUser({
      name: user?.name || '',
      email: user?.email || '',
      telefono: user?.telefono || user?.phone || '',
      direccion: user?.direccion || '',
      identificacion: user?.identificacion || '',
      nit: user?.nit || '',
      edad: user?.edad || undefined,
    });
  };

  const handleLogout = () => {
    Alert.alert(
      'Cerrar Sesión',
      '¿Estás seguro de que quieres cerrar sesión?',
      [
        { text: 'Cancelar', style: 'cancel' },
        { 
          text: 'Cerrar Sesión', 
          style: 'destructive',
          onPress: logout
        },
      ]
    );
  };

  const handleChangePassword = () => {
    // Navegar a pantalla de cambio de contraseña
    Alert.alert('Próximamente', 'Función de cambio de contraseña próximamente');
  };

  const menuItems = [
    {
      title: 'Mis Compras',
      subtitle: `${stats.totalPurchases} compras realizadas`,
      icon: '🛍️',
      onPress: () => Alert.alert('Próximamente', 'Historial de compras próximamente'),
    },
    {
      title: 'Mis Referidos',
      subtitle: stats.referredCount > 0 
        ? `${stats.referredCount} cliente${stats.referredCount !== 1 ? 's' : ''} referido${stats.referredCount !== 1 ? 's' : ''}`
        : 'Clientes que recomendaste',
      icon: '👥',
      badge: stats.referredCount > 0 ? stats.referredCount : undefined,
      onPress: () => navigation.navigate('Referidos' as never),
    },
    {
      title: 'Billetera Virtual',
      subtitle: `Saldo: Q${stats.walletBalance.toFixed(2)}${stats.walletBalance > 0 ? ' ✅' : ' ⚠️'}`,
      icon: '💳',
      onPress: () => {
        Alert.alert(
          'Billetera Virtual', 
          `Saldo actual: Q${stats.walletBalance.toFixed(2)}\n\n¿Qué deseas hacer?`,
          [
            { 
              text: 'Canjear Saldo', 
              onPress: () => navigation.navigate('Search' as never)
            },
            { text: 'Actualizar Saldo', onPress: loadUserStats },
            { text: 'Cerrar', style: 'cancel' }
          ]
        );
      },
    },
    {
      title: 'Favoritos',
      subtitle: 'Productos guardados',
      icon: '❤️',
      onPress: () => navigation.navigate('Favorites' as never),
    },
    {
      title: 'Cambiar Contraseña',
      subtitle: 'Actualizar tu contraseña',
      icon: '🔐',
      onPress: handleChangePassword,
    },
  ];

  if (!user) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>No hay usuario autenticado</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScrollView style={styles.scrollContent}>
        {/* Mensaje de bienvenida */}
        <View style={styles.welcomeContainer}>
          <Text style={styles.welcomeText}>👋 ¡Hola, {user.name}!</Text>
          <Text style={styles.welcomeSubtext}>Bienvenido a tu perfil</Text>
        </View>

      {/* Header del perfil */}
      <View style={styles.header}>
        <View style={styles.avatarContainer}>
          <Image
            source={{ uri: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&size=120&background=e74c3c&color=fff` }}
            style={styles.avatar}
          />
        </View>
        
        <Text style={styles.userName}>{user.name}</Text>
        <Text style={styles.userEmail}>{user.email}</Text>
      </View>

      {/* Formulario de edición */}
      {isEditing ? (
        <View style={styles.editForm}>
          <Input
            label="Nombre completo *"
            value={editedUser.name || ''}
            onChangeText={(value: string) => setEditedUser(prev => ({ ...prev, name: value }))}
            placeholder="Tu nombre completo"
          />
          
          <Input
            label="Email (no editable)"
            value={user?.email || ''}
            placeholder="tu@email.com"
            editable={false}
            style={styles.disabledInput}
          />
          
          <Input
            label="Teléfono"
            value={editedUser.telefono || ''}
            onChangeText={(value: string) => setEditedUser(prev => ({ ...prev, telefono: value }))}
            placeholder="0000-0000"
            keyboardType="phone-pad"
          />

          <Input
            label="Dirección"
            value={editedUser.direccion || ''}
            onChangeText={(value: string) => setEditedUser(prev => ({ ...prev, direccion: value }))}
            placeholder="Tu dirección completa"
            multiline
            numberOfLines={2}
          />

          <Input
            label="Identificación (DPI/Cédula)"
            value={editedUser.identificacion || ''}
            onChangeText={(value: string) => setEditedUser(prev => ({ ...prev, identificacion: value }))}
            placeholder="Número de identificación"
            keyboardType="numeric"
          />

          <Input
            label="NIT"
            value={editedUser.nit || ''}
            onChangeText={(value: string) => setEditedUser(prev => ({ ...prev, nit: value }))}
            placeholder="Número de NIT"
            keyboardType="numeric"
          />

          <Input
            label="Edad"
            value={editedUser.edad?.toString() || ''}
            onChangeText={(value: string) => setEditedUser(prev => ({ ...prev, edad: value ? parseInt(value) : undefined }))}
            placeholder="Tu edad"
            keyboardType="numeric"
          />

          <View style={styles.editButtons}>
            <Button
              title="Cancelar"
              onPress={handleCancelEdit}
              variant="outline"
              style={styles.editButton}
            />
            <Button
              title="Guardar"
              onPress={handleSaveProfile}
              loading={loading}
              style={styles.editButton}
            />
          </View>
        </View>
      ) : (
        <View style={styles.profileInfo}>
          <TouchableOpacity style={styles.editProfileButton} onPress={handleEditProfile}>
            <Text style={styles.editProfileText}>Editar Perfil</Text>
          </TouchableOpacity>
          
          {/* Información adicional del usuario */}
          <View style={styles.userInfoContainer}>
            {user?.telefono && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>📱 Teléfono:</Text>
                <Text style={styles.infoValue}>{user.telefono}</Text>
              </View>
            )}
            
            {user?.direccion && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>🏠 Dirección:</Text>
                <Text style={styles.infoValue}>{user.direccion}</Text>
              </View>
            )}
            
            {user?.departamento && user?.municipio && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>📍 Ubicación:</Text>
                <Text style={styles.infoValue}>{user.departamento}, {user.municipio}</Text>
              </View>
            )}
            
            {user?.nit && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>🆔 NIT:</Text>
                <Text style={styles.infoValue}>{user.nit}</Text>
              </View>
            )}
            
            {user?.monto_acumulado && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>💰 Total gastado:</Text>
                <Text style={styles.infoValue}>Q{parseFloat(user.monto_acumulado).toFixed(2)}</Text>
              </View>
            )}
            
            {user?.fecha_registro && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>📅 Miembro desde:</Text>
                <Text style={styles.infoValue}>
                  {new Date(user.fecha_registro).toLocaleDateString('es-GT')}
                </Text>
              </View>
            )}
            
            {user?.tipo_cliente && (
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>👤 Tipo de cliente:</Text>
                <Text style={[styles.infoValue, user.tipo_cliente === 'premium' ? styles.premiumText : styles.regularText]}>
                  {user.tipo_cliente === 'premium' ? '⭐ Premium' : '🔸 Regular'}
                </Text>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Estadísticas */}
      <View style={styles.statsContainer}>
        <View style={styles.statItem}>
          <Text style={styles.statNumber}>{stats.totalPurchases}</Text>
          <Text style={styles.statLabel}>Compras Realizadas</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={[styles.statNumber, { color: stats.walletBalance > 0 ? '#27ae60' : '#e74c3c' }]}>
            {loadingStats ? '...' : `Q${stats.walletBalance.toFixed(2)}`}
          </Text>
          <Text style={styles.statLabel}>Billetera Virtual</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={[styles.statNumber, { fontSize: 20 }]}>
            {user?.tipo_cliente === 'premium' ? '⭐' : '🔸'}
          </Text>
          <Text style={styles.statLabel}>
            {user?.tipo_cliente === 'premium' ? 'Premium' : 'Regular'}
          </Text>
        </View>
      </View>

      {/* Botón Canjear Saldo */}
      <View style={styles.redeemContainer}>
        <TouchableOpacity 
          style={[
            styles.redeemButton,
            stats.walletBalance === 0 && styles.redeemButtonDisabled
          ]}
          onPress={() => {
            if (stats.walletBalance > 0) {
              navigation.navigate('Search' as never);
            } else {
              Alert.alert(
                'Sin saldo disponible',
                'Acumula saldo con tus compras para poder canjearlo.',
                [{ text: 'Entendido' }]
              );
            }
          }}
          disabled={stats.walletBalance === 0}
        >
          <Text style={styles.redeemIcon}>💳</Text>
          <Text style={styles.redeemText}>
            {stats.walletBalance > 0 ? '🎁 Canjear Saldo' : '⚠️ Sin Saldo para Canjear'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Menu de opciones */}
      <View style={styles.menuContainer}>
        {menuItems.map((item, index) => (
          <TouchableOpacity key={index} style={styles.menuItem} onPress={item.onPress}>
            <View style={styles.menuIcon}>
              <Text style={styles.menuIconText}>{item.icon}</Text>
            </View>
            <View style={styles.menuContent}>
              <View style={styles.menuTitleContainer}>
                <Text style={styles.menuTitle}>{item.title}</Text>
                {item.badge && item.badge > 0 && (
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>{item.badge}</Text>
                  </View>
                )}
              </View>
              <Text style={styles.menuSubtitle}>{item.subtitle}</Text>
            </View>
            <Text style={styles.menuArrow}>›</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Configuración */}
      <View style={styles.settingsContainer}>
        <Text style={styles.sectionTitle}>Configuración</Text>
        
        <View style={styles.settingItem}>
          <View style={styles.settingContent}>
            <Text style={styles.settingTitle}>Notificaciones Push</Text>
            <Text style={styles.settingSubtitle}>Recibir notificaciones de la app</Text>
          </View>
          <Switch
            value={pushNotifications}
            onValueChange={setPushNotifications}
            trackColor={{ false: '#e1e8ed', true: '#e74c3c' }}
            thumbColor={pushNotifications ? '#fff' : '#f4f3f4'}
          />
        </View>
      </View>

      {/* Botón de cerrar sesión */}
      <View style={styles.logoutContainer}>
        <Button
          title="Cerrar Sesión"
          onPress={handleLogout}
          style={styles.logoutButton}
        />
      </View>
    </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  scrollContent: {
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  welcomeContainer: {
    backgroundColor: '#e74c3c',
    paddingVertical: 20,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    marginBottom: 10,
  },
  welcomeText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 5,
  },
  welcomeSubtext: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  header: {
    alignItems: 'center',
    padding: 24,
    backgroundColor: 'white',
    marginBottom: 16,
  },
  avatarContainer: {
    marginBottom: 16,
  },
  avatar: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  userName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 16,
    color: '#7f8c8d',
  },
  profileInfo: {
    backgroundColor: 'white',
    padding: 20,
    marginBottom: 16,
  },
  editProfileButton: {
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: '#e74c3c',
    borderRadius: 25,
  },
  editProfileText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  editForm: {
    backgroundColor: 'white',
    padding: 20,
    marginBottom: 16,
  },
  editButtons: {
    flexDirection: 'row',
    marginTop: 16,
  },
  editButton: {
    flex: 1,
    marginHorizontal: 4,
  },
  statsContainer: {
    flexDirection: 'row',
    backgroundColor: 'white',
    padding: 20,
    marginBottom: 16,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#e74c3c',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  menuContainer: {
    backgroundColor: 'white',
    marginBottom: 16,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f3f4',
  },
  menuIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  menuIconText: {
    fontSize: 18,
  },
  menuContent: {
    flex: 1,
  },
  menuTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  menuTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginRight: 8,
  },
  badge: {
    backgroundColor: '#e74c3c',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    minWidth: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: 'bold',
  },
  menuSubtitle: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  menuArrow: {
    fontSize: 20,
    color: '#bdc3c7',
  },
  settingsContainer: {
    backgroundColor: 'white',
    padding: 20,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 16,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  settingContent: {
    flex: 1,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2c3e50',
    marginBottom: 2,
  },
  settingSubtitle: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  logoutContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  logoutButton: {
    borderColor: '#e74c3c',
  },
  errorText: {
    fontSize: 18,
    color: '#e74c3c',
    textAlign: 'center',
    margin: 20,
  },
  disabledInput: {
    backgroundColor: '#f8f9fa',
    color: '#6c757d',
  },
  userInfoContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
  },
  infoItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#495057',
    flex: 1,
  },
  infoValue: {
    fontSize: 14,
    color: '#2c3e50',
    flex: 2,
    textAlign: 'right',
  },
  premiumText: {
    color: '#f39c12',
    fontWeight: 'bold',
  },
  regularText: {
    color: '#3498db',
    fontWeight: '500',
  },
  redeemContainer: {
    backgroundColor: 'white',
    paddingHorizontal: 20,
    paddingVertical: 16,
    marginBottom: 16,
  },
  redeemButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#27ae60',
    paddingVertical: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  redeemButtonDisabled: {
    backgroundColor: '#95a5a6',
    opacity: 0.6,
  },
  redeemIcon: {
    fontSize: 24,
    marginRight: 8,
  },
  redeemText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
  },
});

